import { useQuery, useMutation, useQueryClient, useInfiniteQuery } from "@tanstack/react-query";
import { customFetch } from "@workspace/api-client-react";

// --- Types ---

export type ActionBand = "NOW" | "NEXT" | "LATER";

export interface OperationsAction {
  id: string;
  source_module: string;
  source_type: string;
  source_id: string;
  action_type: string;
  title: string;
  summary?: string;
  priority_score: number;
  priority_band: ActionBand;
  reason_codes?: string[];
  due_at?: string;
  sla_at?: string;
  status: string; // Uppercase
  assigned_to?: string;
  provenance: string;
  metadata?: Record<string, unknown>;
  sourceLink?: string;
}

export interface ActionsResponse {
  actions: OperationsAction[];
  total: number;
  ruleVersion: string;
}

export interface SearchResult {
  id: string;
  entityType: string;
  group: string;
  title: string;
  matchKind: string;
  relevance: number;
  metadata?: Record<string, unknown>;
  safeActions?: { action: string; requiresExplicitSelection: boolean }[];
}

export interface SearchResponse {
  query: string;
  queryHash: string;
  results: SearchResult[]; // Kept for backwards compat if needed, but groups has the data
  groups: Record<string, SearchResult[]>;
  total: number;
  hasMore: boolean;
}

export interface SearchHistoryItem {
  id: string;
  queryHash: string;
  resultCount: number;
  selectedEntityType: string;
  selectedEntityId: string;
  occurredAt: string;
}

export interface SearchHistoryResponse {
  history: SearchHistoryItem[];
}

export interface ChecklistItem {
  id?: string;
  title: string;
  description?: string;
  required: boolean;
  requiresNote: boolean;
  requiresPhoto: boolean;
  requiresConfirmation: boolean;
  requiresSupervisor: boolean;
  linkedModule?: string;
  linkedAction?: string;
  completedAt?: string;
  completedBy?: string;
  note?: string;
  evidenceRef?: { evidenceId: string; objectPath?: string };
}

export interface ChecklistTemplate {
  id: string;
  name: string;
  description?: string;
  category: string;
  frequency: string;
  triggerType: string;
  appliesToRole?: string;
  appliesToTeam?: string;
  appliesToLocation?: string;
  active: boolean;
  required: boolean;
  version: number;
  items: ChecklistItem[];
}

export interface ChecklistRun {
  id: string;
  templateId: string;
  templateName?: string;
  status: string;
  dueAt?: string;
  assignedTo?: string;
  teamId?: string;
  items: ChecklistItem[];
  completionPercent: number;
}

export interface OperationsEvent {
  id: string;
  event_type: string;
  module: string;
  entity_type: string;
  entity_id: string;
  actor_display_name: string;
  actor_user_id?: string;
  title: string;
  summary?: string;
  severity: string;
  occurred_at: string;
  metadata?: Record<string, unknown>;
  visibility_scope: string;
  source: string;
  provenance: string;
}

export interface FeedResponse {
  events: OperationsEvent[];
  nextCursor?: string;
}

export interface BoardPreset {
  id?: string;
  name: string;
  preset?: string;
  cards?: string[];
  settings?: {
    density?: "comfortable" | "compact";
    theme?: string;
    refreshInterval?: number;
    team?: string;
    location?: string;
  };
  privacyMode?: boolean;
}

// --- Hooks ---

export function useOperationsActions(band?: ActionBand, limit = 50) {
  return useQuery({
    queryKey: ["/api/operations/actions", band, limit],
    queryFn: () => {
      const url = new URL("/api/operations/actions", window.location.origin);
      url.searchParams.set("limit", limit.toString());
      if (band) url.searchParams.set("band", band);
      return customFetch<ActionsResponse>(url.pathname + url.search);
    },
  });
}

export function useTransitionAction() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, command, snoozedUntil, assignedTo, idempotencyKey }: { id: string, command: 'start'|'done'|'snooze'|'assign'|'block'|'reopen', snoozedUntil?: string, assignedTo?: string, idempotencyKey?: string }) => 
      customFetch(`/api/operations/actions/${id}/transition`, {
        method: "POST",
        body: JSON.stringify({ command, snoozedUntil, assignedTo, idempotencyKey }),
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/operations/actions"] });
    },
  });
}

export function useAddActionNote() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, note, idempotencyKey }: { id: string, note: string, idempotencyKey?: string }) => 
      customFetch(`/api/operations/actions/${id}/notes`, {
        method: "POST",
        body: JSON.stringify({ note, idempotencyKey }),
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/operations/actions"] });
    },
  });
}

export function useOperationsSearch(q: string, limit = 20, offset = 0, groups?: string) {
  return useQuery({
    queryKey: ["/api/operations/search", q, limit, offset, groups],
    queryFn: () => {
      const url = new URL("/api/operations/search", window.location.origin);
      url.searchParams.set("q", q);
      url.searchParams.set("limit", limit.toString());
      url.searchParams.set("offset", offset.toString());
      if (groups) url.searchParams.set("groups", groups);
      return customFetch<SearchResponse>(url.pathname + url.search);
    },
    enabled: q.length > 1,
  });
}

export function useOperationsSearchHistory() {
  return useQuery({
    queryKey: ["/api/operations/search/history"],
    queryFn: () => customFetch<SearchHistoryResponse>("/api/operations/search/history"),
  });
}

export function useClearSearchHistory() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => customFetch("/api/operations/search/history", { method: "DELETE" }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["/api/operations/search/history"] }),
  });
}

export function useRecordSearchSelection() {
  return useMutation({
    mutationFn: (body: { entityType: string; entityId: string; query: string; resultCount: number }) =>
      customFetch("/api/operations/search/selection", {
        method: "POST",
        body: JSON.stringify(body),
      }),
  });
}

export function useChecklistTemplates() {
  return useQuery({
    queryKey: ["/api/operations/checklists/templates"],
    queryFn: () => customFetch<{ templates: ChecklistTemplate[] }>("/api/operations/checklists/templates"),
  });
}

export function useChecklistRuns(limit = 50, status?: string, teamId?: string) {
  return useQuery({
    queryKey: ["/api/operations/checklists/runs", limit, status, teamId],
    queryFn: () => {
      const url = new URL("/api/operations/checklists/runs", window.location.origin);
      url.searchParams.set("limit", limit.toString());
      if (status) url.searchParams.set("status", status);
      if (teamId) url.searchParams.set("teamId", teamId);
      return customFetch<{ runs: ChecklistRun[] }>(url.pathname + url.search);
    },
  });
}

export function useCompleteChecklistItem() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ runId, itemId, note, evidenceRef, confirmed }: { runId: string; itemId: string; note?: string; evidenceRef?: { evidenceId: string; objectPath?: string }; confirmed?: boolean }) =>
      customFetch(`/api/operations/checklists/runs/${runId}/items/${itemId}/complete`, {
        method: "POST",
        body: JSON.stringify({ note, evidenceRef, confirmed }),
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/operations/checklists/runs"] });
    },
  });
}

export function useCreateChecklistTemplate() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: Partial<ChecklistTemplate>) =>
      customFetch("/api/operations/checklists/templates", {
        method: "POST",
        body: JSON.stringify(body),
      }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["/api/operations/checklists/templates"] }),
  });
}

export function useUpdateChecklistTemplate() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, body }: { id: string; body: Partial<ChecklistTemplate> }) =>
      customFetch(`/api/operations/checklists/templates/${id}`, {
        method: "PUT",
        body: JSON.stringify(body),
      }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["/api/operations/checklists/templates"] }),
  });
}

export function useCreateChecklistRun() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: { templateId: string; dueAt?: string; assignedTo?: string; teamId?: string }) =>
      customFetch("/api/operations/checklists/runs", {
        method: "POST",
        body: JSON.stringify(body),
      }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["/api/operations/checklists/runs"] }),
  });
}

export function useOperationsFeed(limit = 50, modules?: string, teamId?: string, from?: string, to?: string) {
  return useInfiniteQuery({
    queryKey: ["/api/operations/feed", limit, modules, teamId, from, to],
    queryFn: async ({ pageParam }) => {
      const url = new URL("/api/operations/feed", window.location.origin);
      url.searchParams.set("limit", limit.toString());
      if (pageParam) url.searchParams.set("cursor", pageParam);
      if (modules) url.searchParams.set("modules", modules);
      if (teamId) url.searchParams.set("teamId", teamId);
      if (from) url.searchParams.set("from", from);
      if (to) url.searchParams.set("to", to);
      return customFetch<FeedResponse>(url.pathname + url.search);
    },
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (lastPage) => lastPage.nextCursor,
  });
}

export function useOperationsStatusSummary(keys?: string, refetchInterval?: number) {
  return useQuery({
    queryKey: ["/api/operations/status-summary", keys],
    queryFn: () => {
      const url = new URL("/api/operations/status-summary", window.location.origin);
      if (keys) url.searchParams.set("keys", keys);
      return customFetch<{ generatedAt: string; summaries: Record<string, { value: string | number; label: string; metadata?: any; generatedAt: string; expiresAt?: string }> }>(url.pathname + url.search);
    },
    refetchInterval,
  });
}

export function useBoardPresets() {
  return useQuery({
    queryKey: ["/api/operations/board-presets"],
    queryFn: () => customFetch<{ presets: BoardPreset[] }>("/api/operations/board-presets"),
  });
}

export function useSaveBoardPreset() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: BoardPreset) =>
      customFetch("/api/operations/board-presets", {
        method: "POST",
        body: JSON.stringify(body),
      }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["/api/operations/board-presets"] }),
  });
}

export function useOperationsFrictionSummary(days = 7) {
  return useQuery({
    queryKey: ["/api/operations/friction/summary", days],
    queryFn: () => {
      const url = new URL("/api/operations/friction/summary", window.location.origin);
      url.searchParams.set("days", days.toString());
      return customFetch<any>(url.pathname + url.search);
    },
  });
}
